// src/components/StaffPortal.jsx
//
// "Mes incidents" for conciergerie team members, who aren't Keycloak users
// (documentations/conciergerie-plan.md). Reached from their personal link
// (?t=<token>) or a 7-day link in an alert e-mail (?t=s.<...>&incident=<id>).
// The token is taken out of the address bar straight away; a personal one
// is kept on this device, a signed alert link only for this tab session.
// Backed by backend/routes/staffPortal.ts (X-Staff-Token header).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Container, Typography, Box, Button, Paper, Stack, Chip, Alert, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const STORE_KEY = 'qrproof:staffToken';
const PRIORITY_COLOR = { URGENT: 'error', HIGH: 'warning', NORMAL: 'info', LOW: 'default' };

const safe = (fn) => { try { return fn(); } catch { return null; } };

function takeTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('t');
  const incident = Number(params.get('incident')) || null;
  if (fromUrl) {
    if (fromUrl.startsWith('s.')) safe(() => sessionStorage.setItem(STORE_KEY, fromUrl));
    else safe(() => localStorage.setItem(STORE_KEY, fromUrl));
    window.history.replaceState(null, '', window.location.pathname);
  }
  const token = fromUrl || safe(() => sessionStorage.getItem(STORE_KEY)) || safe(() => localStorage.getItem(STORE_KEY));
  return { token, incident };
}

export default function StaffPortal() {
  const { t, i18n } = useTranslation();
  const [{ token, incident: focusId }] = useState(takeTokenFromUrl);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [resolving, setResolving] = useState(null);
  const [comment, setComment] = useState('');
  const focusRef = useRef(null);

  const call = useCallback(async (path, options = {}) => {
    const r = await fetch(`${API_BASE_URL}/v1/staff-portal${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', 'X-Staff-Token': token },
    });
    if (!r.ok) throw Object.assign(new Error(`HTTP ${r.status}`), { status: r.status });
    return r.json();
  }, [token]);

  const load = useCallback(() => {
    if (!token) return;
    call('/me').then(setData).catch((err) => setError(err.status === 401 ? t('staffPortal.badLink') : t('staffPortal.loadError')));
  }, [token, call, t]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { focusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }); }, [data]);

  const act = async (incident, action, extra = {}) => {
    setBusyId(incident.id);
    setError('');
    try {
      await call(`/incidents/${incident.id}`, { method: 'PUT', body: JSON.stringify({ action, ...extra }) });
      load();
    } catch {
      setError(t('staffPortal.actionError'));
    } finally {
      setBusyId(null);
    }
  };

  if (!token) {
    return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="info">{t('staffPortal.noLink')}</Alert></Container>;
  }
  if (error && !data) {
    return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  }
  if (!data) return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>;

  const manager = data.me.role === 'MANAGER';
  const fmtDate = (d) => (d ? new Date(d).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' }) : '');

  const card = (i, done) => (
    <Paper key={i.id} ref={i.id === focusId ? focusRef : undefined} variant="outlined"
      sx={{ p: 2, outline: i.id === focusId ? '2px solid' : 'none', outlineColor: 'primary.main' }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Chip size="small" color={PRIORITY_COLOR[i.priority]} label={t(`alerts.priority.${i.priority}`)} />
        <Typography sx={{ fontWeight: 700 }}>{t(`alerts.category.${i.category}`)}</Typography>
        <Typography color="text.secondary">— {i.facility_name}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Chip size="small" variant="outlined" label={t(`alerts.status.${i.status}`)} />
      </Stack>
      {i.comment && <Typography sx={{ mb: 1 }}>« {i.comment} »</Typography>}
      <Typography variant="body2" color="text.secondary">
        {t('alerts.reportedAt', { date: fmtDate(i.created_at) })}
        {i.assigned_name && ` · ${t('staffPortal.assignedTo', { name: i.assigned_name })}`}
      </Typography>
      {done && i.resolution_comment && (
        <Typography variant="body2" color="text.secondary">{i.resolution_comment}</Typography>
      )}

      {!done && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          {manager && (
            <Select size="small" displayEmpty value={i.assigned_staff_id ?? ''} disabled={busyId === i.id}
              onChange={(e) => act(i, 'assign', { staffId: Number(e.target.value) })}>
              <MenuItem value="" disabled>{t('staffPortal.assign')}</MenuItem>
              {data.team.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.id === data.me.id ? t('staffPortal.me') : m.name}</MenuItem>
              ))}
            </Select>
          )}
          {['OPEN', 'ASSIGNED'].includes(i.status) && (
            <Button size="small" variant="outlined" disabled={busyId === i.id} onClick={() => act(i, 'start')}>
              {t('alerts.start')}
            </Button>
          )}
          <Button size="small" variant="contained" color="success" disabled={busyId === i.id}
            onClick={() => { setResolving(i); setComment(''); }}>
            {t('alerts.resolve')}
          </Button>
        </Stack>
      )}
      {done && manager && i.status === 'RESOLVED' && (
        <Button size="small" sx={{ mt: 1 }} disabled={busyId === i.id} onClick={() => act(i, 'close')}>{t('alerts.close')}</Button>
      )}
    </Paper>
  );

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4">{t('staffPortal.title')}</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t('staffPortal.hello', { name: data.me.name })} · {t(`team.roles.${data.me.role}`)}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Typography variant="h6" sx={{ mb: 1 }}>{t('staffPortal.open')}</Typography>
      <Stack spacing={2} sx={{ mb: 4 }}>
        {data.open.length === 0 && <Typography color="text.secondary">{t('staffPortal.nothingOpen')}</Typography>}
        {data.open.map((i) => card(i, false))}
      </Stack>

      {data.done.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>{t('staffPortal.done')}</Typography>
          <Stack spacing={2}>{data.done.map((i) => card(i, true))}</Stack>
        </>
      )}

      <Dialog open={Boolean(resolving)} onClose={() => setResolving(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('alerts.resolveTitle')}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={2} sx={{ mt: 1 }} label={t('alerts.resolveComment')}
            value={comment} inputProps={{ maxLength: 1000 }} onChange={(e) => setComment(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolving(null)}>{t('alerts.cancel')}</Button>
          <Button variant="contained" color="success"
            onClick={() => { const i = resolving; setResolving(null); act(i, 'resolve', { comment }); }}>
            {t('alerts.resolve')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
